import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchMemberProfile, fetchMemberRecentOrders, patchMemberProfile } from '../services/memberApi';
import { persistMemberUser, readMemberFromStorage, getMemberFirstName } from '../utils/memberSession';
import './MemberPerfilPage.css';

const MAX_AVATAR_BYTES = 420000;

const MemberPerfilPage = () => {
  const { t, i18n } = useTranslation();
  const [sessionOk, setSessionOk] = useState(() => !!readMemberFromStorage());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [saveError, setSaveError] = useState('');
  const [orders, setOrders] = useState([]);

  const [form, setForm] = useState({
    first_name: '',
    second_name: '',
    first_last_name: '',
    second_last_name: '',
    phone: '',
    country: '',
    city: '',
    profile_address: ''
  });
  const [email, setEmail] = useState('');
  const [handle, setHandle] = useState('');
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [avatarDirty, setAvatarDirty] = useState(false);

  const mergeUserToStorage = useCallback((member) => {
    if (!member || typeof member !== 'object') return;
    persistMemberUser({
      id: member.id,
      email: member.email,
      name: member.name,
      handle: member.handle,
      avatar_data: member.avatar_data || null,
      first_name: String(member.first_name || '').trim() || getMemberFirstName(member)
    });
  }, []);

  const loadData = useCallback(async () => {
    if (!readMemberFromStorage()) {
      setSessionOk(false);
      setLoading(false);
      return;
    }
    setLoadError('');
    setLoading(true);
    try {
      const [profRes, ordRes] = await Promise.all([
        fetchMemberProfile(),
        fetchMemberRecentOrders().catch(() => ({ data: { orders: [] } }))
      ]);
      const member = profRes?.data?.member;
      if (!member) {
        setLoadError(t('memberProfile.loadError'));
        setSessionOk(false);
        return;
      }
      setSessionOk(true);
      setEmail(member.email || '');
      setHandle(member.handle || '');
      setForm({
        first_name: member.first_name || '',
        second_name: member.second_name || '',
        first_last_name: member.first_last_name || '',
        second_last_name: member.second_last_name || '',
        phone: member.phone || '',
        country: member.country || '',
        city: member.city || '',
        profile_address: member.profile_address || ''
      });
      setAvatarPreview(member.avatar_data || null);
      setAvatarDirty(false);
      mergeUserToStorage(member);
      const list = ordRes?.data?.orders;
      setOrders(Array.isArray(list) ? list : []);
    } catch (e) {
      console.error(e);
      setLoadError(e.response?.data?.message || t('memberProfile.loadError'));
      if (e.response?.status === 401) {
        setSessionOk(false);
      }
    } finally {
      setLoading(false);
    }
  }, [mergeUserToStorage, t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setSaveError('');
  };

  const onAvatarFile = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setSaveError(t('memberProfile.avatarTypeError'));
      return;
    }
    if (file.size > MAX_AVATAR_BYTES) {
      setSaveError(t('memberProfile.avatarSizeError'));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const result = typeof reader.result === 'string' ? reader.result : '';
      if (result.length > 650000) {
        setSaveError(t('memberProfile.avatarSizeError'));
        return;
      }
      setAvatarPreview(result);
      setAvatarDirty(true);
      setSaveError('');
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const clearAvatar = () => {
    setAvatarPreview(null);
    setAvatarDirty(true);
    setSaveError('');
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setSaveError('');
    try {
      const body = {
        first_name: form.first_name.trim(),
        second_name: form.second_name.trim() || null,
        first_last_name: form.first_last_name.trim(),
        second_last_name: form.second_last_name.trim() || null,
        phone: form.phone.trim() || null,
        country: form.country.trim() || null,
        city: form.city.trim() || null,
        profile_address: form.profile_address.trim() || null
      };
      if (avatarDirty) {
        body.avatar_data = avatarPreview || null;
      }
      const res = await patchMemberProfile(body);
      const member = res?.data?.member;
      if (member) {
        mergeUserToStorage(member);
        setAvatarPreview(member.avatar_data || null);
        setAvatarDirty(false);
      }
    } catch (err) {
      console.error(err);
      setSaveError(err.response?.data?.message || t('memberProfile.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const formatMoney = (n) => {
    const v = typeof n === 'number' ? n : parseFloat(String(n));
    if (Number.isNaN(v)) return String(n);
    return new Intl.NumberFormat(i18n.language === 'en' ? 'en-US' : 'es-CO', {
      style: 'currency',
      currency: 'COP',
      maximumFractionDigits: 0
    }).format(v);
  };

  const formatDate = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      return d.toLocaleDateString(i18n.language === 'en' ? 'en-US' : 'es-CO', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return String(iso);
    }
  };

  if (!sessionOk && !loading) {
    return (
      <div className="member-perfil-page">
        <p className="member-perfil-no-session">
          {t('memberProfile.noSession')}{' '}
          <button
            type="button"
            className="member-perfil-link"
            onClick={() => {
              window.history.pushState({}, '', '/comunidad');
              window.dispatchEvent(new PopStateEvent('popstate'));
            }}
          >
            {t('memberProfile.goCommunity')}
          </button>
        </p>
      </div>
    );
  }

  return (
    <div className="member-perfil-page">
      {loading ? (
        <p className="member-perfil-loading">{t('memberProfile.loading')}</p>
      ) : (
        <form className="member-perfil-card" onSubmit={onSubmit}>
          <div className="member-perfil-avatar-block">
            <div className="member-perfil-photo-placeholder">
              {avatarPreview ? (
                <img src={avatarPreview} alt="" className="member-perfil-avatar-img" />
              ) : (
                <img src="/images/brand/logo-corto.jpg" alt="" className="member-perfil-logo-dim" />
              )}
            </div>
            <div className="member-perfil-avatar-actions">
              <label className="member-perfil-file-label">
                <input type="file" accept="image/jpeg,image/png,image/gif,image/webp" onChange={onAvatarFile} className="member-perfil-file-input" />
                {t('memberProfile.changePhoto')}
              </label>
              {avatarPreview && (
                <button type="button" className="member-perfil-remove-photo" onClick={clearAvatar}>
                  {t('memberProfile.removePhoto')}
                </button>
              )}
            </div>
          </div>

          <h1 className="member-perfil-title">{form.first_name || form.first_last_name ? `${form.first_name} ${form.first_last_name}`.trim() : email}</h1>
          <p className="member-handle">@{handle || 'miembro'}</p>
          <p className="member-email">{email}</p>

          {loadError && <p className="member-perfil-error">{loadError}</p>}
          {saveError && <p className="member-perfil-error">{saveError}</p>}

          <p className="member-perfil-section-title">{t('memberProfile.personalData')}</p>
          <div className="member-perfil-grid">
            <label className="member-perfil-field">
              <span>{t('memberProfile.firstName')}</span>
              <input name="first_name" value={form.first_name} onChange={onChange} required autoComplete="given-name" />
            </label>
            <label className="member-perfil-field">
              <span>{t('memberProfile.secondName')}</span>
              <input name="second_name" value={form.second_name} onChange={onChange} autoComplete="additional-name" />
            </label>
            <label className="member-perfil-field">
              <span>{t('memberProfile.firstLastName')}</span>
              <input name="first_last_name" value={form.first_last_name} onChange={onChange} required autoComplete="family-name" />
            </label>
            <label className="member-perfil-field">
              <span>{t('memberProfile.secondLastName')}</span>
              <input name="second_last_name" value={form.second_last_name} onChange={onChange} autoComplete="family-name" />
            </label>
            <label className="member-perfil-field member-perfil-field-full">
              <span>{t('memberProfile.phone')}</span>
              <input name="phone" value={form.phone} onChange={onChange} autoComplete="tel" />
            </label>
            <label className="member-perfil-field">
              <span>{t('memberProfile.country')}</span>
              <input name="country" value={form.country} onChange={onChange} autoComplete="country-name" />
            </label>
            <label className="member-perfil-field">
              <span>{t('memberProfile.city')}</span>
              <input name="city" value={form.city} onChange={onChange} autoComplete="address-level2" />
            </label>
            <label className="member-perfil-field member-perfil-field-full">
              <span>{t('memberProfile.address')}</span>
              <textarea name="profile_address" value={form.profile_address} onChange={onChange} rows={3} autoComplete="street-address" />
            </label>
          </div>

          <p className="member-perfil-privacy">{t('memberProfile.privacyNote')}</p>

          <button type="submit" className="member-perfil-save" disabled={saving}>
            {saving ? t('memberProfile.saving') : t('memberProfile.save')}
          </button>

          <section className="member-perfil-orders">
            <h2 className="member-perfil-orders-title">{t('memberProfile.recentOrders')}</h2>
            {orders.length === 0 ? (
              <p className="member-perfil-orders-empty">{t('memberProfile.noOrders')}</p>
            ) : (
              <ul className="member-perfil-order-list">
                {orders.map((o) => (
                  <li key={o.id} className="member-perfil-order-row">
                    <div>
                      <strong>{o.orderNumber}</strong>
                      <span className="member-perfil-order-date">{formatDate(o.createdAt)}</span>
                    </div>
                    <div className="member-perfil-order-meta">
                      <span>{formatMoney(o.total)}</span>
                      <span className="member-perfil-order-status">{o.status}</span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </form>
      )}
    </div>
  );
};

export default MemberPerfilPage;
